<template>
  <LLConfirmModalTransition>
    <div class="StudyNewPageUnfinished">
      <StudyNewPageUnfinishedError
        v-if="isErrorModalVisible"
        :sending-states="questionSendingStates"
        :questions="startedQuestions"
        :question-indexes="questionIndexes"
        @cancel="$emit('close')"
      />
      <StudyNewPageUnfinishedDefaultValue
        v-if="isDefaultValueResponsesModalVisible"
        :sending-states="questionSendingStates"
        :questions="startedQuestions"
        :question-indexes="questionIndexes"
        @cancel="onCancel"
        @submit="submitDefaultValue"
        @skip="onSkip"
      />
      <StudyNewPageUnfinishedContent
        v-if="isContentModalVisible"
        :sending-states="questionSendingStates"
        :questions="startedQuestions"
        :question-indexes="questionIndexes"
        @cancel="onCancel"
        @submit="submit"
        @skip="onSkip"
      />
      <div class="StudyNewPageUnfinished__messages">
        <StudyConversationMessage
          v-for="question in startedQuestions"
          ref="message"
          :key="question.id"
          :message="savedMessage(question.id)"
          :options="messageOptions(question.id)"
          send-using-store
          :stream-parameters="streamParameters(question.id)"
        />
      </div>
    </div>
  </LLConfirmModalTransition>
</template>

<script>
import { StudyModel } from '@/models/study'
import StudyNewPageUnfinishedContent from '@/components/pages/study-new/unfinished/StudyNewPageUnfinishedContent.vue'
import StudyNewPageUnfinishedError from '@/components/pages/study-new/unfinished/StudyNewPageUnfinishedError.vue'
import LLConfirmModalTransition from '@/components/common/LLConfirmModal/LLConfirmModalTransition.vue'
import StudyConversationMessage from '@/components/modules/study/StudyConversationMessage.vue'
import {
  StudyConversationMessageOptionsModel,
  StudyConversationMessageType
} from '@/models/studyConversationMessage/studyConversationMessageOptions'
import { StudyConversationMessageStreamParametersModel } from '@/models/studyConversationMessage/studyConversationMessageStreamParameters'
import StudyNewPageUnfinishedDefaultValue from '@/components/pages/study-new/unfinished/StudyNewPageUnfinishedDefaultValue.vue'
import MessageSendingStates from '@/components/pages/study-new/unfinished/messageSendingStates.json'

export default {
  name: 'StudyNewPageUnfinished',
  components: {
    StudyNewPageUnfinishedDefaultValue,
    StudyConversationMessage,
    LLConfirmModalTransition,
    StudyNewPageUnfinishedError,
    StudyNewPageUnfinishedContent
  },
  props: {
    study: { type: StudyModel, required: true },
    /**
     * @type {QuestionModel[]}
     */
    questions: { type: Array, required: true, default: () => [] },
    conversationStreams: { type: Array, required: true, default: () => [] },
    questionIndexes: { type: Object, default: () => {} }
  },
  data() {
    return {
      isContentModalVisible: true,
      isDefaultValueResponsesModalVisible: false,
      questionSendingStates: {},
      isPending: false,
      completedQuestions: 0,
      startedQuestions: [],
      isErrorModalVisible: false
    }
  },
  computed: {
    MessageSendingStates() {
      return MessageSendingStates
    }
  },
  mounted() {
    this.startedQuestions = [...this.questions]
  },
  methods: {
    savedMessage(questionId) {
      return this.$store.getters['studyMessages/savedMessage']({ questionId: questionId })
    },
    streamParameters(questionId) {
      return new StudyConversationMessageStreamParametersModel({
        question: this.startedQuestions.find((question) => question.id === questionId),
        prospect: this.$store.getters['user/userObject'],
        studyId: this.study.id
      })
    },
    messageOptions(questionId) {
      const conversationStream = this.conversationStreams.find(
        (conversationStream) => conversationStream.questionId === questionId
      )
      return new StudyConversationMessageOptionsModel({
        type:
          conversationStream?.messages?.filter((message) => !message.deletedAt)?.length === 0
            ? StudyConversationMessageType.ANSWER
            : StudyConversationMessageType.NEW_COMMENT
      })
    },
    onCompleteQuestion() {
      this.completedQuestions++
      console.log(this.completedQuestions)
      if (this.completedQuestions === this.startedQuestions.length) {
        this.complete()
      }
    },
    onCancel() {
      if (Object.values(this.questionSendingStates).includes(MessageSendingStates.ERROR)) {
        this.showErrorMessageModal()
        return
      }
      this.$emit('close')
    },
    onSkip() {
      if (Object.values(this.questionSendingStates).includes(MessageSendingStates.ERROR)) {
        this.showErrorMessageModal()
        return
      }
      this.$emit('close', { withCallback: true })
    },
    complete() {
      this.isContentModalVisible = false
      this.isDefaultValueResponsesModalVisible = false
      if (Object.values(this.questionSendingStates).includes(MessageSendingStates.HAS_DEFAULT_VALUE)) {
        this.showDefaultValueResponsesModal()
        return
      }
      if (Object.values(this.questionSendingStates).includes(MessageSendingStates.ERROR)) {
        this.showErrorMessageModal()
        return
      }
      this.$emit('close', { withCallback: true })
    },
    submitDefaultValue() {
      this.submit({ forceWithDefaultValue: true })
    },
    submit({ forceWithDefaultValue } = {}) {
      this.completedQuestions = 0
      this.isPending = true
      this.$refs.message.forEach((messageComponent) => {
        if (
          !messageComponent.message?.questionId ||
          this.questionSendingStates[messageComponent.message.questionId] === MessageSendingStates.SUCCESS ||
          this.questionSendingStates[messageComponent.message.questionId] === MessageSendingStates.ERROR
        ) {
          this.onCompleteQuestion()
          return
        }
        const questionId = messageComponent.message.questionId
        if (
          messageComponent.$refs.editor.localMessage?.pollResponse?.hasDefaultValue === true &&
          !forceWithDefaultValue
        ) {
          this.$set(this.questionSendingStates, questionId, MessageSendingStates.HAS_DEFAULT_VALUE)
          this.onCompleteQuestion()
          return
        }
        if (!messageComponent.$refs.editor.isValid) {
          this.$set(this.questionSendingStates, questionId, MessageSendingStates.ERROR)
          this.onCompleteQuestion()
          return
        }
        this.$set(this.questionSendingStates, questionId, MessageSendingStates.SENDING)
        try {
          messageComponent
            .send({ throwError: true })
            .then(() => {
              this.$set(this.questionSendingStates, questionId, MessageSendingStates.SUCCESS)
              this.onCompleteQuestion()
            })
            .catch((e) => {
              console.log(e)
              this.$set(this.questionSendingStates, questionId, MessageSendingStates.ERROR)
              this.onCompleteQuestion()
            })
        } catch (e) {
          console.log(e)
          this.$set(this.questionSendingStates, questionId, MessageSendingStates.ERROR)
          this.onCompleteQuestion()
        }
      })
    },
    showDefaultValueResponsesModal() {
      this.isContentModalVisible = false
      this.isDefaultValueResponsesModalVisible = true
    },
    showErrorMessageModal() {
      this.isContentModalVisible = false
      this.isDefaultValueResponsesModalVisible = false
      this.isErrorModalVisible = true
    }
  }
}
</script>

<style scoped lang="scss">
.StudyNewPageUnfinished {
  &__messages {
    @apply hidden;
  }
}
</style>
